import { create } from "zustand";

export type LogSource = "companion" | "backend" | "frontend";

export interface DebugLogEntry {
  id: number;
  timestamp: string;
  level: string;
  source: LogSource;
  module: string;
  message: string;
  metadata?: Record<string, unknown>;
}

interface DebugLogFilter {
  levels: Set<string>;
  sources: Set<LogSource>;
  module: string | null;
  search: string;
}

interface DebugLogState {
  entries: DebugLogEntry[];
  filter: DebugLogFilter;
  isOpen: boolean;
  isPaused: boolean;
  panelHeight: number;
  push: (entry: DebugLogEntry) => void;
  pushBatch: (batch: DebugLogEntry[]) => void;
  clear: () => void;
  setFilter: (update: Partial<DebugLogFilter>) => void;
  toggleLevel: (level: string) => void;
  toggleSource: (source: LogSource) => void;
  toggleOpen: () => void;
  togglePause: () => void;
  setPanelHeight: (h: number) => void;
}

const MAX_ENTRIES = 2000;
const ALL_LEVELS = new Set(["info", "warn", "error"]);
const ALL_SOURCES: Set<LogSource> = new Set(["companion", "backend", "frontend"]);

function trimEntries(entries: DebugLogEntry[]): DebugLogEntry[] {
  if (entries.length <= MAX_ENTRIES) return entries;
  return entries.slice(entries.length - MAX_ENTRIES);
}

let idCounter = 0;

export function makeLogEntry(
  level: string,
  source: LogSource,
  module: string,
  message: string,
  metadata?: Record<string, unknown>,
): DebugLogEntry {
  return {
    id: idCounter++,
    timestamp: new Date().toLocaleTimeString("en-GB", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    } as Intl.DateTimeFormatOptions),
    level,
    source,
    module,
    message,
    metadata,
  };
}

export const useDebugLogStore = create<DebugLogState>((set) => ({
  entries: [],
  filter: {
    levels: new Set(ALL_LEVELS),
    sources: new Set(ALL_SOURCES),
    module: null,
    search: "",
  },
  isOpen: false,
  isPaused: false,
  panelHeight: 250,

  push: (entry) =>
    set((state) => {
      if (state.isPaused) return state;
      const next = [...state.entries, entry];
      return { entries: trimEntries(next) };
    }),

  pushBatch: (batch) =>
    set((state) => {
      if (state.isPaused) return state;
      const next = [...state.entries, ...batch];
      return { entries: trimEntries(next) };
    }),

  clear: () => set({ entries: [] }),

  setFilter: (update) =>
    set((state) => ({
      filter: { ...state.filter, ...update },
    })),

  toggleLevel: (level) =>
    set((state) => {
      const next = new Set(state.filter.levels);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return { filter: { ...state.filter, levels: next } };
    }),

  toggleSource: (source: LogSource) =>
    set((state) => {
      const next = new Set(state.filter.sources);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return { filter: { ...state.filter, sources: next } };
    }),

  toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),

  togglePause: () => set((state) => ({ isPaused: !state.isPaused })),

  setPanelHeight: (h) => set({ panelHeight: h }),
}));

export function useFilteredLogs(): DebugLogEntry[] {
  const entries = useDebugLogStore((s) => s.entries);
  const filter = useDebugLogStore((s) => s.filter);

  return entries.filter((e) => {
    if (!filter.levels.has(e.level)) return false;
    if (!filter.sources.has(e.source)) return false;
    if (filter.module && e.module !== filter.module) return false;
    if (
      filter.search &&
      !e.message.toLowerCase().includes(filter.search.toLowerCase())
    )
      return false;
    return true;
  });
}
